import Decimal from 'decimal.js-light';

import { Route } from './0x.types';

export interface LiquiditySourceMultiHop {
  name: 'MultiHop';
  proportion: string;
  hops: string[];
  intermediateToken?: string;
}

export interface LiquiditySource {
  name: string;
  proportion: string;
}

export function getLiquiditySources(route: Route): LiquiditySource[] {
  const filteredSources: LiquiditySource[] = [];

  // The proportions are propagated on each hop, meaning, that each edge contains
  // a "global" proportion number that represents the proportion of each individual edge,
  // relative to all others.
  //
  // We can therefore get the total proportion of each LiquiditySource by summing the ProportionBps
  // grouped by LiquiditySource as well as the total ProportionBps across all fills.
  // The total proportion by LiquiditySource can then be calculated by dividing the grouped proportion value
  // by the total.
  let totalProportion = new Decimal(0);
  const proportionBySource: { [x: string]: Decimal } = {};

  for (const edge of route.fills) {
    if (!proportionBySource[edge.source]) {
      proportionBySource[edge.source] = new Decimal(0);
    }
    const bps = new Decimal(edge.proportionBps);

    totalProportion = totalProportion.add(bps);
    proportionBySource[edge.source] = proportionBySource[edge.source].add(bps);
  }

  const sourceNames = Object.keys(proportionBySource);
  if (totalProportion.isZero()) {
    if (sourceNames.length === 0) return filteredSources;
    const equalShare = new Decimal(1).div(sourceNames.length).toString();
    return sourceNames.map((name) => ({ name, proportion: equalShare }));
  }

  for (const key of Object.keys(proportionBySource)) {
    proportionBySource[key] = proportionBySource[key].div(totalProportion);
  }

  Object.entries(proportionBySource)
    .sort(([, valA], [, valB]) => valA.minus(valB).toNumber())
    .forEach(([name, val]) => {
      filteredSources.push({
        name,
        // the old format was in a decimal representation of the percentage (e.g. 100% is 1)
        // as the new format is in bps, we need to translate bps in decimals
        // 100 bps is 1%, 100% is 1 in decimal
        proportion: val.toString(),
      });
    });

  return filteredSources;
}

/**
 * String used by the data API to represent limit orders placed on Matcha.
 */
export const MATCHA_LIMIT_ORDER = 'Matcha Limit Order';

/**
 * Returns the distinct liquidity sources of the provided Array, ordered by the combined proportion  (desc).
 */
export function distinctSourcesProportionDescending(sources: LiquiditySource[]): string[] {
  const mapped: Record<string, number> = {};

  for (const source of sources) {
    if (!mapped[source.name]) {
      mapped[source.name] = Number(source.proportion);
      continue;
    }
    mapped[source.name] += Number(source.proportion);
  }

  return Object.entries(mapped)
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
}

// ex: uniswap_v3 --> Uniswap V3
export function getLiquiditySourceLabel(liquiditySource: string): string {
  if (liquiditySource === '0x') {
    return '0x RFQ';
  }

  const label = liquiditySource
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return label;
}
